import React from "react";
import {
  Badge,
  Box,
  Icon,
  Text,
  toColor,
  toGlyphName,
  toastManager,
  type Color,
  type GlyphName,
} from "@salestrak/ui";

import { useIssueView } from "#app/components/issue-view";
import { AppearanceMenuRow } from "#app/components/menus/appearance-menu-row";
import { NO_PERMISSION } from "#app/components/menus/disabled-reason";
import {
  DROPDOWN_MENU_PARTS,
  type MenuParts,
} from "#app/components/menus/menu-parts";
import { useAutomations } from "#app/hooks/automations/use-automations";
import { useAutomationsDialogActions } from "#app/lib/automations-dialog-context";
import { useAppData, useWorkspace, type WorkflowRow } from "#app/lib/data";
import { ENTITY_KIND_ICON, WORKFLOW_DEFAULT_GLYPH } from "#app/lib/entity-visuals";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useAutomationsEnabled } from "#app/lib/hooks/use-automations-enabled";
import { useCanCreateProject } from "#app/lib/hooks/use-can-create-project";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useWorkflowMutations } from "#app/lib/hooks/use-workflow-mutations";
import { useStagesDialogActions } from "#app/lib/stages-dialog-context";
import { copyText } from "#app/lib/utils/copy-actions";
import { resultMessage, toastMutationError } from "#app/lib/utils/toast-errors";
import type { Omittable } from "@salestrak/ui";
import { workflowHref } from "#app/lib/utils/entity-hrefs";

/**
 * THE workflow action menu — one roster serving every surface that manages a
 * workflow: the masthead crumb's right-click, the sidebar row's right-click
 * and its "..." dropdown, and the workflows tables' kebab + row right-click.
 * Hosts differ only in menu kind (`parts`) and in where their dialogs live.
 *
 * Permission keys DISABLE rows rather than hide them (the view-tab menu's
 * house rule): a menu whose rows come and go by role makes the same row a
 * different row depending on who opened it. A disabled row says why in a
 * tooltip (`DisabledReason`).
 */

/** The fields the menu reads. Any workflow row shape (the list row, the
 *  workspace row) satisfies it. */
export type WorkflowMenuTarget = Pick<
  WorkflowRow,
  "workflowId" | "name" | "icon" | "archived" | "isDefault"
>;

/**
 * The shared handlers + permission gates behind the menu, wired ONCE per
 * hosting screen (never per row — the mutation hooks live here). Everything a
 * dialog does not own goes through this: appearance, archive, default, rename
 * commit, navigation, copy link.
 */
export function useWorkflowMenuActions() {
  const { refetch: refetchAppData } = useAppData();
  const { activeWorkspace } = useWorkspace();
  const { push } = useAppNavigate();
  const { updateWorkflow, setDefaultWorkflow } = useWorkflowMutations();
  const { openStagesDialog } = useStagesDialogActions();
  const { openDialog: openAutomationsDialog } = useAutomationsDialogActions();
  const { openCreateIssue } = useIssueView();
  const automationsEnabled = useAutomationsEnabled();

  const canEdit = usePermission("manage.edit_workflows");
  const canDelete = usePermission("delete.workflows");
  const canCreateWorkflow = usePermission("create.workflows");
  const canCreateProject = useCanCreateProject();

  const urlPath = activeWorkspace?.urlPath ?? "";

  return React.useMemo(
    () => ({
      canEdit,
      canDelete,
      canCreateWorkflow,
      canCreateProject,
      automationsEnabled,
      copyLink(workflowId: string) {
        void copyText(workflowHref(urlPath, workflowId), "Link");
      },
      /** Appearance edits live-save the full pair — `icon` replaces the whole
       *  IconInput, so a color pick must re-send the glyph and vice versa. */
      async saveAppearance(workflowId: string, icon: GlyphName, color: Color) {
        try {
          await updateWorkflow(workflowId, { icon: { icon, color } });
          refetchAppData();
        } catch (err) {
          toastMutationError(err, "Failed to update workflow appearance");
        }
      },
      async rename(workflowId: string, name: string) {
        try {
          const result = await updateWorkflow(workflowId, { name });
          // A refusal (a name another workflow holds) says why.
          if (result?.__typename !== "Workflow") {
            toastManager.add({
              title: resultMessage(result) ?? "Failed to rename workflow",
              type: "error",
            });
            return;
          }
          // The sidebar rows, the crumb and the tables all read this list.
          refetchAppData();
        } catch (err) {
          toastMutationError(err, "Failed to rename workflow");
        }
      },
      async toggleArchive(
        workflow: Pick<WorkflowMenuTarget, "workflowId" | "archived">,
      ) {
        const archived = !workflow.archived;
        try {
          const result = await updateWorkflow(workflow.workflowId, { archived });
          if (result?.__typename !== "Workflow") {
            toastManager.add({
              title:
                resultMessage(result) ??
                (archived ? "Failed to archive workflow" : "Failed to unarchive workflow"),
              type: "error",
            });
            return;
          }
          refetchAppData();
          // An archived workflow leaves the sidebar, so the toast says so.
          toastManager.add({
            title: archived ? "Workflow archived" : "Workflow unarchived",
            type: "success",
          });
        } catch (err) {
          toastMutationError(
            err,
            archived
              ? "Failed to archive workflow"
              : "Failed to unarchive workflow",
          );
        }
      },
      async setDefault(workflowId: string) {
        try {
          const result = await setDefaultWorkflow(workflowId);
          if (result?.__typename !== "Workflow") {
            toastManager.add({
              title: resultMessage(result) ?? "Failed to set default workflow",
              type: "error",
            });
            return;
          }
          // Nothing on the menu's own row changes, so the toast confirms it.
          toastManager.add({ title: "Workflow set as default", type: "success" });
        } catch (err) {
          toastMutationError(err, "Failed to set default workflow");
        }
      },
      openStages(workflowId: string) {
        openStagesDialog(workflowId);
      },
      openSettings(workflowId: string) {
        push(`/settings/workflows/${workflowId}`);
      },
      openAutomations(workflowId: string) {
        openAutomationsDialog({ type: "manage-for-workflow", workflowId });
      },
      createProject(workflowId: string, source: string) {
        openCreateIssue({ workflowId, source });
      },
    }),
    [
      canEdit,
      canDelete,
      canCreateWorkflow,
      canCreateProject,
      automationsEnabled,
      urlPath,
      updateWorkflow,
      setDefaultWorkflow,
      refetchAppData,
      openStagesDialog,
      openAutomationsDialog,
      openCreateIssue,
      push,
    ],
  );
}

export type WorkflowMenuActions = ReturnType<typeof useWorkflowMenuActions>;

// Its own component so the automations query fires when the menu opens (the
// popup mounts its items on open), reading the same cache entry the masthead's
// automations popover already primed.
function AutomationsMenuItem({
  parts,
  workflowId,
  onOpen,
}: {
  parts: MenuParts;
  workflowId: string;
  onOpen: () => void;
}) {
  const { Item } = parts;
  const where = React.useMemo(() => ({ workflowId }), [workflowId]);
  const { automations } = useAutomations(where);
  const activeCount = automations.filter((a) => a.enabled).length;

  return (
    <Item onClick={onOpen}>
      <Icon color="muted" name="zap" size="md" />
      <Text>Automations</Text>
      {activeCount > 0 ? (
        <>
          <Box fill="flex" />
          <Badge>{activeCount}</Badge>
        </>
      ) : null}
    </Item>
  );
}

type WorkflowMenuItemsProps = Omittable<{
  /** Menu part set of the hosting menu kind. Defaults to the dropdown parts. */
  parts?: MenuParts;
  workflow: WorkflowMenuTarget;
  /** From `useWorkflowMenuActions`, called once per hosting screen. */
  actions: WorkflowMenuActions;
  /** Backend-resolved share gate (`viewerCanManageSharing`) — never derived
   *  from viewerAccessLevel. */
  canShare: boolean;
  /** Opens the host's share dialog. */
  onShare: () => void;
  /** Starts the host's rename affordance — inline where the row can swap to
   *  an input, the rename dialog where it cannot. */
  onRename: () => void;
  /** Opens the host's delete confirmation dialog. */
  onRequestDelete: () => void;
  /** Opens the host's create-workflow dialog. Omit it to drop the "Create
   *  new" submenu entirely — a host whose page already carries its own
   *  create button (the workflows table) leaves it out. */
  onRequestCreateWorkflow?: () => void;
  /** Analytics origin for "Create new > Project". */
  createIssueSource?: string;
}>;

export function renderWorkflowMenuItems({
  parts = DROPDOWN_MENU_PARTS,
  workflow,
  actions,
  canShare,
  onShare,
  onRename,
  onRequestDelete,
  onRequestCreateWorkflow,
  createIssueSource = "workflow_menu",
}: WorkflowMenuItemsProps) {
  const { Item, Separator, Sub, SubTrigger, SubPopup } = parts;
  const workflowId = workflow.workflowId;

  const icon = toGlyphName(workflow.icon.icon) ?? WORKFLOW_DEFAULT_GLYPH;
  const color = toColor(workflow.icon.color) ?? "blue";

  const gatedItem = (opts: {
    allowed: boolean;
    reason?: string | undefined;
    variant?: "default" | "destructive";
    onClick: () => void;
    children: React.ReactNode;
  }) => (
    <Item
      {...(opts.variant ? { variant: opts.variant } : {})}
      {...(opts.allowed
        ? { onClick: opts.onClick }
        : { disabled: true, disabledReason: opts.reason ?? NO_PERMISSION })}
    >
      {opts.children}
    </Item>
  );

  return (
    <>
      {gatedItem({
        allowed: actions.canEdit,
        onClick: onRename,
        children: (
          <>
            <Icon color="muted" name="pencil" size="md" />
            <Text>Rename</Text>
          </>
        ),
      })}
      <Item onClick={() => actions.copyLink(workflowId)}>
        <Icon color="muted" name="link" size="md" />
        <Text>Copy link</Text>
      </Item>
      <Separator />
      {onRequestCreateWorkflow && (
        <Sub>
          <SubTrigger
            disabled={!actions.canCreateProject && !actions.canCreateWorkflow}
            disabledReason={NO_PERMISSION}
          >
            <Icon color="muted" name="plus" size="md" />
            <Text>Create new</Text>
          </SubTrigger>
          <SubPopup>
            {gatedItem({
              allowed: actions.canCreateProject,
              onClick: () =>
                actions.createProject(workflowId, createIssueSource),
              children: (
                <>
                  <Icon color="muted" name={ENTITY_KIND_ICON.issue} size="md" />
                  <Text>Project</Text>
                </>
              ),
            })}
            {gatedItem({
              allowed: actions.canCreateWorkflow,
              onClick: onRequestCreateWorkflow,
              children: (
                <>
                  <Icon
                    color="muted"
                    name={ENTITY_KIND_ICON.workflow}
                    size="md"
                  />
                  <Text>Workflow</Text>
                </>
              ),
            })}
          </SubPopup>
        </Sub>
      )}
      <AppearanceMenuRow
        disabled={!actions.canEdit}
        panel={{
          color,
          defaultIcon: WORKFLOW_DEFAULT_GLYPH,
          icon,
          onColorChange: (next) =>
            void actions.saveAppearance(workflowId, icon, next),
          onIconChange: (next) =>
            void actions.saveAppearance(workflowId, next, color),
          // Offered only while the glyph has left the kind's own: a Reset
          // that would change nothing is noise.
          onReset:
            icon === WORKFLOW_DEFAULT_GLYPH
              ? undefined
              : () =>
                  void actions.saveAppearance(
                    workflowId,
                    WORKFLOW_DEFAULT_GLYPH,
                    color,
                  ),
        }}
        parts={parts}
        target={{ kind: "workflow", workflow }}
      />
      {gatedItem({
        allowed: actions.canEdit,
        onClick: () => actions.openStages(workflowId),
        children: (
          <>
            <Icon color="muted" name="circle-circle" size="md" />
            <Text>Stages</Text>
          </>
        ),
      })}
      {actions.automationsEnabled ? (
        <AutomationsMenuItem
          parts={parts}
          workflowId={workflowId}
          onOpen={() => actions.openAutomations(workflowId)}
        />
      ) : null}
      <Separator />
      {gatedItem({
        allowed: actions.canEdit,
        onClick: () => actions.openSettings(workflowId),
        children: (
          <>
            <Icon color="muted" name="settings" size="md" />
            <Text>Workflow settings</Text>
          </>
        ),
      })}
      {gatedItem({
        allowed: actions.canEdit && !workflow.isDefault,
        reason: actions.canEdit ? "Already the default workflow" : undefined,
        onClick: () => void actions.setDefault(workflowId),
        children: (
          <>
            <Icon color="muted" name="star" size="md" />
            <Text>Set as default</Text>
          </>
        ),
      })}
      <Separator />
      {gatedItem({
        allowed: canShare,
        onClick: onShare,
        children: (
          <>
            <Icon color="muted" name="users" size="md" />
            <Text>Sharing &amp; Permissions</Text>
          </>
        ),
      })}
      <Separator />
      {gatedItem({
        allowed: actions.canEdit,
        onClick: () => void actions.toggleArchive(workflow),
        children: (
          <>
            <Icon color="muted" name="archive" size="md" />
            <Text>{workflow.archived ? "Unarchive" : "Archive"}</Text>
          </>
        ),
      })}
      {gatedItem({
        allowed: actions.canDelete && !workflow.isDefault,
        variant: "destructive",
        reason: actions.canDelete
          ? "The default workflow can't be deleted"
          : undefined,
        onClick: onRequestDelete,
        children: (
          <>
            <Icon name="trash" size="md" />
            <Text>Delete</Text>
          </>
        ),
      })}
    </>
  );
}
